import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Wallet, CheckCircle2, Inbox, AlertTriangle, CalendarClock,
  Send, UserPlus, Pencil, Download, Archive, XCircle, Image as ImageIcon, Gift,
  Receipt, MoreHorizontal, RefreshCw, CircleSlash, X,
} from "lucide-react";
import AppLayout from "../../components/AppLayout";
import StatsCard from "../../components/StatsCard";
import StatusBadge from "../../components/StatusBadge";
import DueChip from "../../components/DueChip";
import SendReminderDialog from "../../components/SendReminderDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import api, {
  formatCurrency, formatDate, daysUntil, API_BASE, getAccessToken, formatMethod,
} from "../../lib/api";
import { useTerms } from "../../lib/BrandContext";
import { useSmartBack } from "../../lib/backNavigation";
import { toast } from "sonner";

const METHODS = ["venmo", "zelle", "cashapp", "cash", "check", "other"];

/* ─────────────────────────── */
/* Edit charge dialog            */
/* ─────────────────────────── */
function EditChargeDialog({ open, onOpenChange, charge, onSaved }) {
  const [form, setForm] = useState({ title: "", amount: "", due_date: "", description: "" });
  const [saving, setSaving] = useState(false);
  // Impact preview for the amount-change warning. Loaded lazily on open so
  // we don't add a second network call to every fee-detail render.
  const [impact, setImpact] = useState(null);
  useEffect(() => {
    if (!open || !charge) return;
    setForm({
      title: charge.title || "",
      amount: String(charge.amount ?? ""),
      due_date: charge.due_date || "",
      description: charge.description || "",
    });
    setImpact(null);
    api.get(`/charges/${charge.id}/edit-impact`)
      .then((r) => setImpact(r.data))
      .catch(() => setImpact(null)); // Warning is progressive-enhancement.
  }, [open, charge]);

  const originalAmount = Number(charge?.amount ?? NaN);
  const nextAmount = parseFloat(form.amount);
  const amountChanged =
    !Number.isNaN(nextAmount) &&
    !Number.isNaN(originalAmount) &&
    nextAmount !== originalAmount;
  // Only surface the warning when the amount is actually changing AND
  // real activity exists on this fee. A clean fee (0 records) doesn't
  // need any warning — nothing is at stake.
  const showAmountWarning =
    amountChanged &&
    !!impact &&
    (impact.updates_on_save.total > 0 || impact.frozen_on_save.total > 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/charges/${charge.id}`, {
        title: form.title,
        amount: nextAmount,
        due_date: form.due_date,
        description: form.description,
      });
      toast.success("Fee updated");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-charge-dialog">
        <DialogHeader>
          <DialogTitle>Edit fee</DialogTitle>
          <DialogDescription>Update the title, amount, due date, or description.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="h-11" data-testid="edit-charge-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="h-11" data-testid="edit-charge-amount" />
            </div>
            <div>
              <Label>Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} className="h-11" data-testid="edit-charge-due-date" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="edit-charge-description" />
          </div>

          {/* Fee frequency — read-only in edit per product spec: coaches see
              WHICH mode this fee is on, but changing recurrence mid-cycle
              would require refactoring the generation/series logic and is
              intentionally out of scope. To switch a fee's frequency,
              cancel it and create a new one with the right frequency. */}
          <div
            className="rounded-lg border border-ink-100 bg-ink-100/40 p-3"
            data-testid="edit-charge-frequency-readonly"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">Fee frequency</p>
            <p className="mt-1 text-sm font-semibold text-ink-900">
              {charge?.recurring_monthly ? "Monthly recurring" : "One-time"}
            </p>
            <p className="mt-0.5 text-xs text-ink-500">
              {charge?.recurring_monthly
                ? "This fee auto-generates every month. To change the frequency, cancel this fee and create a new one."
                : "This fee is charged once. To change the frequency, cancel this fee and create a new one."}
            </p>
          </div>

          {/* Amount-change disclosure. Only appears when the coach is
              actually changing the amount AND the fee has activity. Plain-
              English breakdown of what gets updated vs preserved so
              nothing about the accounting model changes silently. */}
          {showAmountWarning ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="edit-charge-amount-warning"
            >
              <p className="font-semibold">
                Changing this fee from {formatCurrency(originalAmount)} to {formatCurrency(nextAmount)}
              </p>
              {impact.updates_on_save.total > 0 ? (
                <p className="mt-1" data-testid="edit-charge-updates-line">
                  <span className="font-semibold">Will update {impact.updates_on_save.total}</span>{" "}
                  open assignment{impact.updates_on_save.total === 1 ? "" : "s"} to the new amount
                  {impact.updates_on_save.rejected > 0
                    ? ` (${impact.updates_on_save.unpaid} unpaid, ${impact.updates_on_save.rejected} rejected)`
                    : ""}.
                </p>
              ) : null}
              {impact.frozen_on_save.total > 0 ? (
                <p className="mt-1" data-testid="edit-charge-frozen-line">
                  <span className="font-semibold">Will NOT change {impact.frozen_on_save.total}</span>{" "}
                  record{impact.frozen_on_save.total === 1 ? "" : "s"} — completed activity stays at the
                  amount that was already recorded
                  {(() => {
                    const parts = [];
                    if (impact.frozen_on_save.confirmed) parts.push(`${impact.frozen_on_save.confirmed} confirmed`);
                    if (impact.frozen_on_save.submitted) parts.push(`${impact.frozen_on_save.submitted} pending review`);
                    if (impact.frozen_on_save.waived)    parts.push(`${impact.frozen_on_save.waived} waived`);
                    return parts.length ? ` (${parts.join(", ")}).` : ".";
                  })()}
                </p>
              ) : null}
              <p className="mt-2 text-xs text-amber-800">
                To collect a difference from players who already paid, add a supplemental fee.
              </p>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="edit-charge-submit">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── */
/* Add players dialog            */
/* ─────────────────────────── */
function AddPlayersDialog({ open, onOpenChange, charge, currentPlayerIds, onAdded }) {
  const { terms } = useTerms();
  const [eligible, setEligible] = useState([]);
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open || !charge?.team_id) return;
    setPicked([]);
    setLoading(true);
    api.get(`/players?team_id=${charge.team_id}`)
      .then((r) => setEligible((r.data || []).filter((p) => !currentPlayerIds.includes(p.id))))
      .catch(() => setEligible([]))
      .finally(() => setLoading(false));
  }, [open, charge?.team_id, currentPlayerIds]);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const submit = async () => {
    if (picked.length === 0) return;
    setSaving(true);
    try {
      const r = await api.post(`/charges/${charge.id}/add-players`, { player_ids: picked });
      toast.success(`Added ${r.data.created} ${r.data.created === 1 ? terms.athlete : terms.athletes}`);
      onOpenChange(false);
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not add ${terms.athletes}`);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-players-dialog">
        <DialogHeader>
          <DialogTitle>Add {terms.athletes} to this fee</DialogTitle>
          <DialogDescription>
            Only {terms.athletes} on <span className="font-semibold">{charge?.team?.name || `this ${terms.team}`}</span> who aren&apos;t
            already on this fee are shown.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-ink-200 bg-white">
          {loading ? (
            <p className="p-4 text-sm text-ink-500">Loading…</p>
          ) : eligible.length === 0 ? (
            <p className="p-4 text-sm text-ink-500">
              All {terms.athletes} on this {terms.team} are already on this fee.
            </p>
          ) : (
            <ul className="divide-y divide-ink-100">
              {eligible.map((p) => (
                <li key={p.id}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-ink-100/60" data-testid={`add-players-row-${p.id}`}>
                    <Checkbox checked={picked.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
                    <span className="text-sm font-semibold text-ink-900">
                      {p.first_name} {p.last_name}{p.jersey ? <span className="ml-1 text-ink-500">#{p.jersey}</span> : null}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button
            disabled={saving || picked.length === 0}
            onClick={submit}
            className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover"
            data-testid="add-players-submit"
          >
            <UserPlus className="mr-1 h-4 w-4" />
            Add {picked.length || ""} {picked.length === 1 ? terms.athlete : terms.athletes}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── */
/* Record payment dialog (reuse-friendly inline copy) */
/* ─────────────────────────── */
function RecordPaymentDialog({ open, onOpenChange, record, onDone }) {
  const [method, setMethod] = useState("venmo");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setMethod("venmo"); setNote(""); } }, [open, record?.id]);
  const save = async () => {
    if (!record?.id) return;
    setSaving(true);
    try {
      await api.post(`/payments/${record.id}/manual-mark`, { method, coach_note: note || "Recorded by coach" });
      toast.success("Payment marked paid");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cd-record-payment-dialog">
        <DialogHeader>
          <DialogTitle>Mark Paid</DialogTitle>
          <DialogDescription>
            Mark this fee as paid on behalf of the parent. Use this when they paid you directly.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-ink-100/60 p-3 text-sm">
            <p className="font-semibold text-ink-900">
              {record?.player?.first_name} {record?.player?.last_name}
            </p>
            <p className="text-ink-500">{formatCurrency(record?.amount)} due {formatDate(record?.charge?.due_date)}</p>
          </div>
          <div>
            <Label>How they paid</Label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 h-11 w-full rounded-lg border border-ink-200 bg-white px-3" data-testid="cd-record-method-select">
              {METHODS.map((m) => <option key={m} value={m}>{formatMethod(m)}</option>)}
            </select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. handed me cash at practice" data-testid="cd-record-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover" data-testid="cd-record-submit">
            <CheckCircle2 className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Mark Paid"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── */
/* Reject dialog                 */
/* ─────────────────────────── */
function RejectDialog({ open, onOpenChange, record, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open, record?.id]);
  const submit = async () => {
    if (!record?.id) return;
    setSaving(true);
    try {
      await api.post(`/payments/${record.id}/reject`, { coach_note: reason || "Rejected by coach" });
      toast.success("Payment rejected");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cd-reject-payment-dialog">
        <DialogHeader>
          <DialogTitle>Reject payment</DialogTitle>
          <DialogDescription>Add a short reason so the parent knows what to fix.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-ink-100/60 p-3 text-sm">
            <p className="font-semibold text-ink-900">{record?.player?.first_name} {record?.player?.last_name}</p>
            <p className="text-ink-500">{formatCurrency(record?.amount)} via {formatMethod(record?.method) || "—"}</p>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Example: I couldn't verify this payment. Please upload a screenshot or contact me." data-testid="cd-reject-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="h-11 bg-rose-600 font-semibold text-white hover:bg-rose-700" data-testid="cd-reject-submit">
            <XCircle className="mr-1 h-4 w-4" /> {saving ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── */
/* One payment row inside a charge */
/* ─────────────────────────── */
function ChargePaymentRow({ r, onConfirm, onRecord, onReject, onWaive, onRemove }) {
  const showMethod = !!r.method && ["submitted", "confirmed"].includes(r.status);
  const token = getAccessToken();
  const receiptUrl = r.receipt_path
    ? `${API_BASE}/files/${r.receipt_path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`
    : null;
  // Pick which timestamp to show
  let timing = null;
  if (r.status === "submitted" && r.submitted_at) timing = `Submitted ${formatDate(r.submitted_at)}`;
  else if (r.status === "confirmed" && r.confirmed_at) timing = `Confirmed ${formatDate(r.confirmed_at)}`;
  else if (r.status === "rejected" && r.submitted_at) timing = `Submitted ${formatDate(r.submitted_at)} · rejected`;

  // Safe removal is only offered for records with no financial activity —
  // mirrors the backend audit-freeze policy at DELETE /charges/{id}/players/{pid}.
  const canRemove = ["unpaid", "rejected", "canceled"].includes(r.status);

  return (
    <li className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-4" data-testid={`cd-row-${r.id}`}>
      <div className="min-w-0 md:flex-[2]">
        <p className="font-semibold text-ink-900 truncate" data-testid={`cd-row-player-${r.id}`} title={`${r.player?.first_name || ""} ${r.player?.last_name || ""}`.trim()}>
          {r.player?.first_name} {r.player?.last_name}
          {r.player?.jersey ? <span className="ml-1 text-ink-500">#{r.player.jersey}</span> : null}
        </p>
        <p className="text-xs text-ink-500 line-clamp-2 break-words">{timing || "—"}</p>
        {r.note ? <p className="mt-1 text-xs text-ink-500 line-clamp-2 break-words">Parent: {r.note}</p> : null}
      </div>
      <div className="md:w-28 md:text-right">
        <p className="font-heading text-lg font-bold text-ink-900">{formatCurrency(r.amount)}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:w-56 md:justify-end">
        {showMethod ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-2.5 py-1 text-xs font-semibold text-ink-700">
            <Receipt className="h-3 w-3" />
            {formatMethod(r.method)}
          </span>
        ) : null}
        <StatusBadge status={r.status} />
      </div>
      <div className="flex flex-wrap items-center gap-2 md:w-72 md:justify-end">
        {r.status === "submitted" ? (
          <>
            {receiptUrl ? (
              <a href={receiptUrl} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center gap-1 rounded-md border border-ink-300 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100" data-testid={`cd-receipt-${r.id}`}>
                <ImageIcon className="h-4 w-4" /> Receipt
              </a>
            ) : null}
            <Button size="sm" className="h-9 bg-emerald-600 font-semibold text-white hover:bg-emerald-700" onClick={() => onConfirm(r.id)} data-testid={`cd-confirm-${r.id}`}>
              <CheckCircle2 className="mr-1 h-4 w-4" /> Confirm
            </Button>
            <Button size="sm" variant="outline" className="h-9 border-rose-300 font-semibold text-rose-700 hover:bg-rose-50" onClick={() => onReject(r)} data-testid={`cd-reject-${r.id}`}>
              <XCircle className="mr-1 h-4 w-4" /> Reject
            </Button>
          </>
        ) : null}
        {["unpaid", "rejected"].includes(r.status) ? (
          <>
            <Button size="sm" className="h-9 bg-brand font-semibold text-white hover:bg-brand-hover" onClick={() => onRecord(r)} data-testid={`cd-record-${r.id}`}>
              <Wallet className="mr-1 h-4 w-4" /> Mark Paid
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-9 px-2.5 font-semibold text-ink-600 hover:bg-ink-100 hover:text-ink-900"
              onClick={() => onWaive(r.id)}
              data-testid={`cd-waive-${r.id}`}
              title="Waive this fee for this player"
            >
              <Gift className="mr-1 h-4 w-4" /> Waive
            </Button>
          </>
        ) : null}
        {canRemove ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-9 px-2.5 font-semibold text-ink-500 hover:bg-rose-50 hover:text-rose-700"
            onClick={() => onRemove(r)}
            data-testid={`cd-remove-${r.id}`}
            title="Remove this player from this fee"
          >
            <X className="mr-1 h-4 w-4" /> Remove
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/* ─────────────────────────── */
/* Main page                    */
/* ─────────────────────────── */
export default function ChargeDetail() {
  const { id } = useParams();
  const { terms } = useTerms();
  const back = useSmartBack("/coach/charges", "Fees");
  const navigate = useNavigate();
  const [charge, setCharge] = useState(null);
  const [team, setTeam] = useState(null);
  const [records, setRecords] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [reminderOpen, setReminderOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addPlayersOpen, setAddPlayersOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'archive' | 'cancel'
  const [recordRecord, setRecordRecord] = useState(null);
  const [rejectRecord, setRejectRecord] = useState(null);
  // Remove-player confirmation. `null` = closed. When set, holds the
  // payment_record whose (charge, player) pairing the coach wants to
  // dissolve. Backend rejects the delete if any payment activity exists,
  // but we also guard the button rendering with the same `canRemove`
  // check so the coach never sees a Remove button they can't use.
  const [removeRecord, setRemoveRecord] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const c = await api.get(`/charges/${id}`);
      setCharge(c.data);
      const [t, recs] = await Promise.all([
        api.get(`/teams/${c.data.team_id}`),
        api.get(`/payments?charge_id=${id}`),
      ]);
      setTeam(t.data);
      setRecords(recs.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load fee");
      setCharge(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const isOverdueCharge = !!(charge?.due_date && charge.due_date < todayIso);

  // Totals + counts
  const stats = useMemo(() => {
    const expected = records.reduce((s, r) => s + (r.amount || 0), 0);
    const confirmed = records.filter((r) => r.status === "confirmed").reduce((s, r) => s + (r.amount || 0), 0);
    const submittedAmt = records.filter((r) => r.status === "submitted").reduce((s, r) => s + (r.amount || 0), 0);
    const submittedCount = records.filter((r) => r.status === "submitted").length;
    const unpaidCount = records.filter((r) => ["unpaid", "rejected"].includes(r.status)).length;
    const overdueCount = isOverdueCharge ? unpaidCount : 0;
    return { expected, confirmed, submittedAmt, submittedCount, unpaidCount, overdueCount };
  }, [records, isOverdueCharge]);

  // Apply status filter
  const filtered = useMemo(() => {
    if (statusFilter === "all") return records;
    return records.filter((r) => r.status === statusFilter);
  }, [records, statusFilter]);

  const currentPlayerIds = useMemo(() => records.map((r) => r.player_id), [records]);

  const doAction = async (recordId, action, extra = {}) => {
    try {
      await api.post(`/payments/${recordId}/${action}`, extra);
      const verb = { confirm: "confirmed", waive: "waived" }[action] || action;
      toast.success(`Payment ${verb}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const archiveOrCancel = async (action) => {
    try {
      await api.post(`/charges/${charge.id}/${action}`);
      toast.success(action === "archive" ? "Fee archived" : "Fee canceled");
      setConfirmAction(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const stopRecurring = async () => {
    try {
      await api.post(`/charges/${charge.id}/stop-recurring`);
      toast.success("Recurring stopped", {
        description: "No more monthly copies will be generated. Existing fees stay intact.",
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not stop recurring");
    }
  };

  const downloadCsv = () => {
    const token = getAccessToken();
    const auth = token ? `&auth=${encodeURIComponent(token)}` : "";
    window.open(`${API_BASE}/reports/csv?charge_id=${id}${auth}`, "_blank");
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex h-32 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </AppLayout>
    );
  }
  if (!charge) {
    return (
      <AppLayout>
        <Link to={back.to} className="inline-flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900">
          <ArrowLeft className="h-4 w-4" /> Back to {back.label}
        </Link>
        <p className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="charge-detail-error">
          {error || "Fee not found."}
        </p>
      </AppLayout>
    );
  }

  const isFinal = ["archived", "canceled"].includes(charge.status);
  const d = daysUntil(charge.due_date);
  const dueTone = d != null && d < 0 ? "text-rose-600" : d != null && d <= 3 ? "text-amber-700" : "text-ink-700";

  return (
    <AppLayout>
      <Link to={back.to} className="inline-flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900" data-testid="back-to-charges">
        <ArrowLeft className="h-4 w-4" /> Back to {back.label}
      </Link>

      {/* Header */}
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="label-caps">Fee</p>
          <h1 className="h2" data-testid="charge-detail-title">{charge.title}</h1>
          <p className="mt-1 text-sm text-ink-500">
            {team?.name || "—"} · due {formatDate(charge.due_date)}
            <span className={`ml-2 font-semibold ${dueTone}`}>
              ({d == null ? "—" : d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? "due today" : `in ${d}d`})
            </span>
          </p>
          {charge.description ? <p className="mt-1 text-sm text-ink-700">{charge.description}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={charge.status} />
          {charge.recurring_monthly ? (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-brand-100 bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand"
              data-testid="cd-recurring-badge"
              title="This fee auto-generates monthly"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Recurring monthly
            </span>
          ) : null}
          {charge.recurring_parent_id ? (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-ink-100 bg-ink-100/60 px-2.5 py-1 text-xs font-semibold text-ink-700"
              data-testid="cd-recurring-series-badge"
              title="Part of a recurring monthly series"
            >
              Part of monthly series
            </span>
          ) : null}
          <DueChip dueDate={charge.due_date} chargeStatus={charge.status} />
          <span className="font-heading text-2xl font-bold text-ink-900">{formatCurrency(charge.amount)}</span>
        </div>
      </div>

      {/* Top metrics */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatsCard label="Expected"  value={formatCurrency(stats.expected)}  testid="cd-stat-expected" />
        <StatsCard label="Confirmed" value={formatCurrency(stats.confirmed)} accent="emerald" testid="cd-stat-confirmed" />
        <StatsCard label="Submitted" value={`${stats.submittedCount} · ${formatCurrency(stats.submittedAmt)}`} accent="amber" testid="cd-stat-submitted" />
        <StatsCard label="Unpaid"    value={stats.unpaidCount}    accent="rose" testid="cd-stat-unpaid" />
        <StatsCard label="Overdue"   value={stats.overdueCount}   accent="rose" testid="cd-stat-overdue" />
      </div>

      {/* Action bar */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="cd-actions">
        <Button
          onClick={() => setReminderOpen(true)}
          disabled={isFinal || stats.unpaidCount === 0}
          className="h-11 bg-brand font-semibold text-white hover:bg-brand-hover"
          data-testid="cd-action-remind"
        >
          <Send className="mr-1 h-4 w-4" /> Remind unpaid
          {stats.unpaidCount > 0 ? <span className="ml-1.5 rounded-full bg-white/20 px-2 py-0.5 text-xs">{stats.unpaidCount}</span> : null}
        </Button>
        <Button
          variant="outline"
          onClick={() => setAddPlayersOpen(true)}
          disabled={isFinal}
          className="h-11 border-ink-300 font-semibold"
          data-testid="cd-action-add-players"
        >
          <UserPlus className="mr-1 h-4 w-4" /> Add {terms.athletes}
        </Button>
        <Button
          variant="outline"
          onClick={() => setEditOpen(true)}
          disabled={isFinal}
          className="h-11 border-ink-300 font-semibold"
          data-testid="cd-action-edit"
        >
          <Pencil className="mr-1 h-4 w-4" /> Edit fee
        </Button>
        {charge.recurring_monthly ? (
          <Button
            variant="outline"
            onClick={stopRecurring}
            disabled={isFinal}
            className="h-11 border-ink-300 font-semibold"
            data-testid="cd-action-stop-recurring"
          >
            <CircleSlash className="mr-1 h-4 w-4" /> Stop recurring
          </Button>
        ) : null}
        <Button
          variant="outline"
          onClick={downloadCsv}
          className="h-11 border-ink-300 font-semibold"
          data-testid="cd-export-csv"
        >
          <Download className="mr-1 h-4 w-4" /> Export CSV
        </Button>
        <div className="ml-auto flex gap-2">
          <Button
            variant="ghost"
            onClick={() => setConfirmAction("archive")}
            disabled={isFinal}
            className="h-11 text-ink-700 hover:bg-ink-100"
            data-testid="cd-action-archive"
          >
            <Archive className="mr-1 h-4 w-4" /> Archive
          </Button>
          <Button
            variant="ghost"
            onClick={() => setConfirmAction("cancel")}
            disabled={isFinal}
            className="h-11 text-rose-600 hover:bg-rose-50"
            data-testid="cd-action-cancel"
          >
            <XCircle className="mr-1 h-4 w-4" /> Cancel
          </Button>
        </div>
      </div>

      {/* Players list */}
      <section className="mt-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="h3">Assigned {terms.athletes}</h2>
          <div role="tablist" className="inline-flex w-max overflow-x-auto rounded-full border border-ink-200 bg-ink-100 p-1 text-xs font-semibold">
            {["all", "unpaid", "submitted", "confirmed", "rejected", "waived"].map((s) => {
              const count = s === "all" ? records.length : records.filter((r) => r.status === s).length;
              const active = statusFilter === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3 capitalize transition-colors ${
                    active ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-700"
                  }`}
                  data-testid={`cd-filter-${s}`}
                >
                  {s}
                  {count > 0 ? (
                    <span className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold ${active ? "bg-ink-900 text-white" : "bg-ink-200 text-ink-700"}`}>
                      {count}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3 overflow-hidden rounded-xl border border-ink-100 bg-white shadow-sm">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-500" data-testid="cd-empty">
              No payments in this view.
            </div>
          ) : (
            <ul className="divide-y divide-ink-100">
              {filtered.map((r) => (
                <ChargePaymentRow
                  key={r.id}
                  r={r}
                  onConfirm={(rid) => doAction(rid, "confirm")}
                  onRecord={(rec) => setRecordRecord(rec)}
                  onReject={(rec) => setRejectRecord(rec)}
                  onWaive={(rid) => doAction(rid, "waive", { coach_note: "Waived" })}
                  onRemove={(rec) => setRemoveRecord(rec)}
                />
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Dialogs */}
      <SendReminderDialog
        open={reminderOpen}
        onOpenChange={setReminderOpen}
        charge={charge}
        forceScope="all_unpaid"
      />
      <EditChargeDialog open={editOpen} onOpenChange={setEditOpen} charge={charge} onSaved={load} />
      <AddPlayersDialog
        open={addPlayersOpen}
        onOpenChange={setAddPlayersOpen}
        charge={{ ...charge, team }}
        currentPlayerIds={currentPlayerIds}
        onAdded={load}
      />
      <RecordPaymentDialog
        open={!!recordRecord}
        onOpenChange={(o) => { if (!o) setRecordRecord(null); }}
        record={recordRecord ? { ...recordRecord, charge } : null}
        onDone={load}
      />
      <RejectDialog
        open={!!rejectRecord}
        onOpenChange={(o) => { if (!o) setRejectRecord(null); }}
        record={rejectRecord}
        onDone={load}
      />

      <AlertDialog open={!!removeRecord} onOpenChange={(o) => { if (!o && !removing) setRemoveRecord(null); }}>
        <AlertDialogContent data-testid="cd-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {removeRecord?.player?.first_name} {removeRecord?.player?.last_name} from this fee?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removeRecord?.player?.first_name} will no longer owe {formatCurrency(removeRecord?.amount || 0)} for {charge?.title || "this fee"}.
              This will not remove {removeRecord?.player?.first_name} from the {team?.name || terms.team}, and any other fees or payments are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing} data-testid="cd-remove-cancel">Keep on fee</AlertDialogCancel>
            <AlertDialogAction
              disabled={removing}
              onClick={async () => {
                if (!removeRecord) return;
                setRemoving(true);
                try {
                  await api.delete(`/charges/${charge.id}/players/${removeRecord.player_id}`);
                  toast.success(
                    `${removeRecord.player?.first_name || "Player"} removed from ${charge?.title || "fee"}`
                  );
                  setRemoveRecord(null);
                  await load();
                } catch (e) {
                  const detail = e.response?.data?.detail;
                  const msg = typeof detail === "object" && detail?.message
                    ? detail.message
                    : (typeof detail === "string" ? detail : "Could not remove player from this fee.");
                  toast.error(msg);
                } finally {
                  setRemoving(false);
                }
              }}
              className="bg-rose-600 text-white hover:bg-rose-700"
              data-testid="cd-remove-confirm"
            >
              {removing ? "Removing…" : "Remove from fee"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmAction} onOpenChange={(o) => { if (!o) setConfirmAction(null); }}>
        <AlertDialogContent data-testid="cd-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === "archive" ? "Archive this fee?" : "Cancel this fee?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === "archive"
                ? "Archive keeps the history visible but removes this fee from your active list. You can still see who paid."
                : "Parents who haven't paid will no longer owe this fee. Any submitted or confirmed payments will remain in the payment history."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cd-confirm-cancel">
              {confirmAction === "archive" ? "Keep active" : "Keep Fee"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archiveOrCancel(confirmAction)}
              className={confirmAction === "archive" ? "bg-ink-700 text-white hover:bg-ink-900" : "bg-rose-600 text-white hover:bg-rose-700"}
              data-testid="cd-confirm-action"
            >
              {confirmAction === "archive" ? "Archive fee" : "Cancel Fee"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
